import { GETREQUESTOPTIONS, instance } from '../apiService';

export type FeedbackSurveyScope = 'trial' | 'subscription' | 'global';

export interface FeedbackSurveyPopupOptions {
    emojiText: string;
    emojiAnimation: string;
    layout: string;
    width: number;
    overlay: boolean;
    alignLeft?: boolean;
}

export interface FeedbackSurveyTemplate {
    _id?: string;
    templateKey: string;
    title: string;
    description: string;
    formId: string;
    scope: FeedbackSurveyScope;
    isDefault: boolean;
    popupOptions: FeedbackSurveyPopupOptions;
    order: number;
    defaultDurationDays?: number;
}

export interface FeedbackSurveyDepartmentOption {
    _id: string;
    name: string;
    region?: string;
    isTrial?: boolean;
}

export interface FeedbackSurveyRoleOption {
    _id: string;
    role: string;
    alternativeNames?: Record<string, string>;
}

export interface FeedbackSurveyOptions {
    defaultTemplates: FeedbackSurveyTemplate[];
    defaultPopupOptions: FeedbackSurveyPopupOptions;
    departments: FeedbackSurveyDepartmentOption[];
    roles: FeedbackSurveyRoleOption[];
    regions: string[];
}

export interface CreateFeedbackSurveyPayload {
    templateKey?: string;
    title?: string;
    description?: string;
    formId?: string;
    scope?: FeedbackSurveyScope;
    allSites?: boolean;
    department_ids?: string[];
    roles?: string[];
    regions?: string[];
    includeTrialSites?: boolean;
    startDate?: string;
    durationDays?: number;
    isActive?: boolean;
    allowOverlap?: boolean;
    popupOptions?: Partial<FeedbackSurveyPopupOptions>;
}

export interface FeedbackSurveyOverlapSurvey {
    _id: string;
    title: string;
    startDate?: string;
    endDate?: string;
}

export interface FeedbackSurveyOverlapDepartment {
    _id: string;
    name: string;
    surveys: FeedbackSurveyOverlapSurvey[];
}

export interface FeedbackSurveyOverlapResult {
    hasOverlap: boolean;
    departments: FeedbackSurveyOverlapDepartment[];
}

export interface FeedbackSurvey {
    _id: string;
    templateKey?: string;
    template_id?: string;
    title: string;
    description?: string;
    formId: string;
    scope: FeedbackSurveyScope;
    isDefault: boolean;
    allSites?: boolean;
    department_ids?: FeedbackSurveyDepartmentOption[];
    roles?: string[];
    regions?: string[];
    popupOptions?: FeedbackSurveyPopupOptions;
    includeTrialSites?: boolean;
    startDate?: string;
    durationDays?: number;
    endDate?: string;
    isActive: boolean;
    createdAt?: string;
}

export interface FeedbackSurveyFilters {
    scope?: FeedbackSurveyScope;
    isActive?: boolean;
    department_ids?: string[];
    activeNow?: boolean;
}

const unwrapFeedbackSurveyResponse = <T>(res: any): T => {
    if (res.status >= 400) {
        throw res.data;
    }

    return res.data;
};

export const getFeedbackSurveyOptions =
    async (): Promise<FeedbackSurveyOptions> => {
        const res = await instance.get(
            '/api/feedback-surveys/options',
            GETREQUESTOPTIONS()
    );
    return unwrapFeedbackSurveyResponse<FeedbackSurveyOptions>(res);
};

export const getFeedbackSurveys = async (
    filters: FeedbackSurveyFilters = {}
): Promise<FeedbackSurvey[]> => {
    const res = await instance.get('/api/feedback-surveys', {
        ...GETREQUESTOPTIONS(),
        params: filters,
    });
    return unwrapFeedbackSurveyResponse<FeedbackSurvey[]>(res);
};

export const getAvailableFeedbackSurveys = async (): Promise<FeedbackSurvey[]> => {
    const res = await instance.get(
        '/api/feedback-surveys/available',
        GETREQUESTOPTIONS()
    );
    return unwrapFeedbackSurveyResponse<FeedbackSurvey[]>(res);
};

export const checkFeedbackSurveyOverlaps = async (
    payload: CreateFeedbackSurveyPayload & { excludeSurveyId?: string }
): Promise<FeedbackSurveyOverlapResult> => {
    const res = await instance.post(
        '/api/feedback-surveys/overlaps',
        payload,
        GETREQUESTOPTIONS()
    );
    return unwrapFeedbackSurveyResponse<FeedbackSurveyOverlapResult>(res);
};

export const createFeedbackSurvey = async (
    payload: CreateFeedbackSurveyPayload
) => {
    const res = await instance.post(
        '/api/feedback-surveys',
        payload,
        GETREQUESTOPTIONS()
    );
    return unwrapFeedbackSurveyResponse<FeedbackSurvey>(res);
};

export const updateFeedbackSurvey = async (
    id: string,
    payload: CreateFeedbackSurveyPayload
) => {
    const res = await instance.patch(
        `/api/feedback-surveys/${id}`,
        payload,
        GETREQUESTOPTIONS()
    );
    return unwrapFeedbackSurveyResponse<FeedbackSurvey>(res);
};

export const deleteFeedbackSurvey = async (id: string) => {
    const res = await instance.delete(
        `/api/feedback-surveys/${id}`,
        GETREQUESTOPTIONS()
    );
    return unwrapFeedbackSurveyResponse<void>(res);
};
